import { Suspense, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { NotificationsDrawer } from "@/features/notifications/NotificationsDrawer";
import { useAuthStore } from "@/features/auth/auth.store";
import { useUiStore } from "@/stores/ui.store";
import { markStartup } from "@/lib/startup-perf";
import { PageLoadingSkeleton } from "@/ui/feedback/skeletons";
import { useWarmupCoreData } from "./useWarmupCoreData";

export function AppShell() {
  const status = useAuthStore((s) => s.status);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const { pathname } = useLocation();

  // Pre-fill the most-visited pages' caches in the background so the first
  // click after opening the app paints instantly (see useWarmupCoreData).
  useWarmupCoreData();

  useEffect(() => {
    markStartup("router-ready");
  }, []);

  // Le volet de navigation mobile se referme à chaque changement de page —
  // couvre tous les NavLinks sans toucher la Sidebar.
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname, setSidebarOpen]);

  // ESC ferme le volet, même contrat que les autres drawers de l'app.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen, setSidebarOpen]);

  // "checking" never reaches here — RequireSession (the parent route) renders the
  // startup skeleton — but guard anyway so the shell can't render without a
  // confirmed session if it is ever mounted from another tree.
  if (status === "checking") return null;
  if (status === "anonymous") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-[var(--app-h)] overflow-hidden bg-bg">
      {/* Voile du volet nav mobile — frère de la Sidebar (un parent transformé
          casserait son `fixed`). Au-dessus de la topbar (z-50), sous les
          notifications (990) et les modales (2000). */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Topbar />
        <main className="flex-1 overflow-y-auto overflow-x-hidden scroll-thin">
          {/* Route-level code splitting: the shell stays mounted and interactive
              while the page chunk downloads — only this area shows a skeleton. */}
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <NotificationsDrawer />
    </div>
  );
}
